import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

export interface AssetCategoryMaster {
  id: string
  organizationId: string
  name: string
  code: string
  label: string
  assetType: 'PHYSICAL' | 'DIGITAL'
  icon?: string | null
  sortOrder: number
  isActive: boolean
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

export interface AssetStatusMaster {
  id: string
  organizationId: string
  name: string
  code: string
  label: string
  assetType: 'PHYSICAL' | 'DIGITAL'
  color: string
  sortOrder: number
  isActive: boolean
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

export interface AssetCategoryFilters {
  assetType?: 'PHYSICAL' | 'DIGITAL'
  isActive?: boolean
}

export interface AssetStatusFilters {
  assetType?: 'PHYSICAL' | 'DIGITAL'
  isActive?: boolean
}

// ─── Categories ──────────────────────────────────────────────────────────

export function useAssetCategoryMasters(filters?: AssetCategoryFilters) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['assetCategoryMasters', organization?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ organizationId: organization?.id || '' })
      if (filters?.assetType) params.set('assetType', filters.assetType)
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
      const res = await api.get<any>(`/master-data/asset-categories?${params}`)
      return (res?.data || []) as AssetCategoryMaster[]
    },
    enabled: !!organization?.id && can('ASSETS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useAssetCategoryMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['assetCategoryMasters', organization?.id] })
    queryClient.invalidateQueries({ queryKey: ['assetStatusMasters', organization?.id] })
  }

  const create = useMutation({
    mutationFn: (payload: any) =>
      api.post<any>('/master-data/asset-categories', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      api.patch<any>(`/master-data/asset-categories/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/asset-categories/${id}/status`, { isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const bulkActivate = useMutation({
    mutationFn: ({ assetType, isActive }: { assetType: 'PHYSICAL' | 'DIGITAL'; isActive: boolean }) =>
      api.post<any>('/master-data/asset-categories/bulk-activate', { assetType, isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/master-data/asset-categories/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, bulkActivate, remove }
}

// ─── Statuses ────────────────────────────────────────────────────────────

export function useAssetStatusMasters(filters?: AssetStatusFilters) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['assetStatusMasters', organization?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ organizationId: organization?.id || '' })
      if (filters?.assetType) params.set('assetType', filters.assetType)
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
      const res = await api.get<any>(`/master-data/asset-statuses?${params}`)
      return (res?.data || []) as AssetStatusMaster[]
    },
    enabled: !!organization?.id && can('ASSETS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useAssetStatusMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['assetStatusMasters', organization?.id] })
    queryClient.invalidateQueries({ queryKey: ['assetCategoryMasters', organization?.id] })
  }

  const create = useMutation({
    mutationFn: (payload: Omit<AssetStatusMaster, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) =>
      api.post<any>('/master-data/asset-statuses', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<AssetStatusMaster> }) =>
      api.patch<any>(`/master-data/asset-statuses/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/asset-statuses/${id}/status`, { isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const bulkActivate = useMutation({
    mutationFn: ({ assetType, isActive }: { assetType: 'PHYSICAL' | 'DIGITAL'; isActive: boolean }) =>
      api.post<any>('/master-data/asset-statuses/bulk-activate', { assetType, isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/master-data/asset-statuses/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, bulkActivate, remove }
}
